import { Tour } from "@app/shared/models/tour.type";
import { service_tourService } from "@app/shared/services/tours.service";
import { atom_tourStage, atom_loadingProgress, LoadingProgress } from "@app/shared/state/loading.atom";
import { atom_tourState, atom_borders } from "@app/shared/state/tour.atom";
import { fetchAndMergeUserStops, atom_userAddedPlaces } from "@app/shared/state/user-added-stops.atom";
import { fetchAndMergeSavedStops, atom_allBookmarksState } from "@app/shared/state/filter.atom";
import React, { useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { useSetRecoilState, useRecoilCallback } from "recoil";
import { passiveCollector } from "@app/shared/services/passive-data/passive-data-collector";
import { useRenderTracker } from '@app/shared/debug';


export const InitialDataFetch = React.memo(function InitialDataFetch() {
  useRenderTracker('InitialDataFetch');
  const { tourId } = useParams();
  const setTourStage = useSetRecoilState(atom_tourStage);
  const setBorders = useSetRecoilState(atom_borders);
  const setUserAddedPlaces = useSetRecoilState(atom_userAddedPlaces);
  const setAllBookmarks = useSetRecoilState(atom_allBookmarksState);
  const setLoadingProgress = useSetRecoilState(atom_loadingProgress);
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Batch multiple atom updates into a single render
  const batchSetAtoms = useRecoilCallback(({ set }) => (updates: {
    tour?: Tour;
    borders?: any;
    loadingProgress?: LoadingProgress;
  }) => {
    if (updates.tour) set(atom_tourState, updates.tour);
    if (updates.borders) set(atom_borders, updates.borders);
    if (updates.loadingProgress) set(atom_loadingProgress, updates.loadingProgress);
  }, []);

  // Smooth progress animation for fetching phase (0-63%)
  const startSmoothFetchProgress = () => {
    let currentProgress = 0;
    progressIntervalRef.current = setInterval(() => {
      // Slow down as we approach 63% (never quite reaching it until actual completion)
      const remaining = 60 - currentProgress;
      const increment = Math.max(0.5, remaining * 0.08);
      currentProgress = Math.min(60, currentProgress + increment);
      setLoadingProgress({ phase: 'fetching_tour', percent: Math.round(currentProgress) });
    }, 500);
  };

  const stopSmoothProgress = () => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
  };

  useEffect(() => {
    (async () => {
      if (tourId) {
        setTourStage('loading');
        setLoadingProgress({ phase: 'fetching_tour', percent: 0 });

        try {
          // Phase 1: Fetch tour info AND user data in parallel (0-73%)
          startSmoothFetchProgress();

          const [tour, userStopsResult, savedStopsResult] = await Promise.all([
            service_tourService.getTourByIdNew(tourId),
            fetchAndMergeUserStops(tourId, setUserAddedPlaces).catch((error) => {
              console.error('[InitialDataFetch] Error syncing user stops:', error);
              return [];
            }),
            fetchAndMergeSavedStops(tourId, setAllBookmarks).catch((error) => {
              console.error('[InitialDataFetch] Error syncing saved stops:', error);
              return [];
            }),
          ]);

          stopSmoothProgress();

          if (!tour) {
            throw new Error("No tour found");
          }

          // Phase 3: Loading boundaries in parallel
          let borders = null;
          try {
            borders = await service_tourService.getBorders(tourId);
          } catch (error) {
            console.warn('[InitialDataFetch] Error loading borders:', error);
          }

          const allBorders = borders || [];

          // Batch all atom updates into a single render
          batchSetAtoms({
            tour,
            borders: allBorders,
            loadingProgress: { phase: 'loading_boundaries', percent: 80 },
          });

          // Track tour view in analytics (passiveCollector fires Firebase events internally)
          passiveCollector.trackTourStarted(tour.id, tour.stops?.length);

          // Log tour data for easy copy-paste
          // console.log(JSON.stringify({
          //   city: tour.title,
          //   stops: tour.stops?.map((stop) => ({
          //     id: stop.stop_index,
          //     name: stop.s_title,
          //     lat: stop.stop_location.latitude,
          //     lng: stop.stop_location.longitude,
          //   })),
          // }, null, 2));

        } catch (error) {
          stopSmoothProgress();
          // @ts-ignore
          // eslint-disable-next-line no-alert
          alert("--DGuide Error-- " + error.message) &&
            window.location.reload();
        }
      }
    })();

    return () => stopSmoothProgress();
  }, []);

  return <div className="fragment"></div>;
});
